/**
 * Shop Utilities
 * Contains pure functions for filtering and sorting products.
 */

/**
 * Filters and sorts a list of products based on active filters and sort criteria.
 * 
 * @param {Array} products - The array of product objects from Firestore.
 * @param {Object} filters - Local filters (searchQuery, priceRanges, size, motion).
 * @param {string} filters.searchQuery - The search query string.
 * @param {string} sortBy - The current sort key.
 * @returns {Array} - The processed array.
 */
export function filterAndSortProducts(products, filters, sortBy) {
    let result = [...products];

    // Category Filtering (Multi-select)
    if (filters.categories && filters.categories.length > 0) {
        result = result.filter(p => 
            filters.categories.includes(p.category) || 
            (p.collections && filters.categories.some(cat => p.collections.includes(cat)))
        );
    }

    // 0. Search Filtering: Name, Title, Subtitle, or Description
    if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase().trim();
        result = result.filter(p => 
            p.name?.toLowerCase().includes(query) || 
            p.title?.toLowerCase().includes(query) || 
            p.subtitle?.toLowerCase().includes(query) ||
            p.description?.toLowerCase().includes(query)
        );

        // Prioritize name/title matches over description matches
        result.sort((a, b) => {
            const aNameMatch = (a.name?.toLowerCase().includes(query) || a.title?.toLowerCase().includes(query));
            const bNameMatch = (b.name?.toLowerCase().includes(query) || b.title?.toLowerCase().includes(query));
            
            if (aNameMatch && !bNameMatch) return -1;
            if (!aNameMatch && bNameMatch) return 1;
            return 0;
        });
    }

    // 1. Local Filtering: Price Ranges
    if (filters.priceRanges && filters.priceRanges.length > 0) {
        result = result.filter(p => {
            const price = p.priceValue !== undefined ? p.priceValue : parseFloat(p.price.replace(/[^0-9.-]+/g, ""));
            return filters.priceRanges.some(range => {
                const [min, max] = range.split('–').map(Number);
                return price >= min && price <= max;
            });
        });
    }

    // 2. Local Filtering: Size
    if (filters.size && filters.size.length > 0) {
        result = result.filter(p =>
            filters.size.some(s => p.sizes?.includes(s))
        );
    }

    // 3. Local Filtering: Motion
    if (filters.motion && filters.motion.length > 0) {
        result = result.filter(p =>
            filters.motion.includes(p.motion)
        );
    }

    // 4. Sorting logic
    if (sortBy === 'price-asc') result.sort((a, b) => (a.priceValue || 0) - (b.priceValue || 0));
    else if (sortBy === 'price-desc') result.sort((a, b) => (b.priceValue || 0) - (a.priceValue || 0));
    else if (sortBy === 'newest') {
        result.sort((a, b) => {
            const idA = parseInt(a.id?.replace('prod', '')) || 0;
            const idB = parseInt(b.id?.replace('prod', '')) || 0;
            return idB - idA;
        });
    }

    return result;
}

/**
 * Highlights occurrences of a search query within a given text.
 * @param {string} text - The original text.
 * @param {string} query - The search query to highlight.
 * @returns {string} - The text with matches wrapped in <strong> tags.
 */
export function highlightMatch(text, query) {
    if (!query || !text) return text;

    // Escape special characters in the query for regex
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi'); // 'g' for global, 'i' for case-insensitive

    const parts = text.split(regex);

    return parts.map(part => {
        // Check if the part matches the query (case-insensitive)
        if (part.toLowerCase() === query.toLowerCase()) {
            return `<strong>${part}</strong>`;
        }
        return part;
    }).join('');
}